import type { IReportTable, ReportRowType } from '../../common/Visualization/types';
import type { AnalyticInterval } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { DataSeries } from '@shopify/polaris-viz';
import type { IRMetricKey } from '../../common/type';

import {
  getMetricPreviousValue,
  getMetricValue,
  getPreviousTotalColumnNameByDimensions,
  getTotalColumnNameByDimensions,
} from '../../common/Visualization/utils';

import {
  CHART_COLOR_MAP,
  CHART_COLORS,
  COMPARISON_CHART_COLOR,
  NONE_VALUE,
  SINGLE_CHART_COLOR,
} from '../constants';
import { formatDateRange, getDateQueryVariable, getFormattedByInterval } from '~/modules/analytics';
import { calculatePercentageChange } from '~/modules/page-analytic/modules/analytic';
import { CHART_LABEL_MAP, TIME_KEYS } from '~/modules/report-analytic';

export const TIME_SERIES = 'TIME_SERIES';

type SeriesKey = string;
type DateKey = string;
type SeriesValue = {
  currentMetricValue: number;
  comparisonKey?: string;
  comparisonMetricValue: number;
};
type SeriesMapType = Map<SeriesKey, Map<DateKey, SeriesValue>>;

interface BuildSeriesMapParams {
  data: IReportTable;
  metricKey: IRMetricKey;
  chartKeys: string[];
  templateLabelById: Record<string, string>;
}

interface BuildSeriesMapResult {
  seriesMap: SeriesMapType;
  keysSet: Set<string>;
  seriesKey: Set<string>;
}

const mapChartKeyToLabel = (chartKey: string, templateLabelById: Record<string, string>) => {
  return CHART_LABEL_MAP[chartKey] ?? templateLabelById[chartKey] ?? chartKey;
};
const formatTime = (key: string, value: string) => {
  const isTimeKey = TIME_KEYS?.has(key);
  if (!isTimeKey) return value;

  return getFormattedByInterval(value, key.toLocaleUpperCase() as AnalyticInterval);
};

export function sortSeriesMap<T>(
  inputMap: Map<string, T>,
  noneValue: string,
  timeDimensionKey: string,
): Map<string, T> {
  const isTimeSeries = TIME_KEYS?.has(timeDimensionKey);
  const sortedEntries = Array.from(inputMap.entries()).sort(([keyA], [keyB]) => {
    const isANone = keyA === noneValue;
    const isBNone = keyB === noneValue;

    if (isANone && !isBNone) return 1;
    if (!isANone && isBNone) return -1;
    if (isANone && isBNone) return 0;

    if (isTimeSeries) {
      return new Date(keyA).getTime() - new Date(keyB).getTime();
    }

    return keyA.localeCompare(keyB);
  });

  if (!isTimeSeries) {
    return new Map(sortedEntries);
  }

  return new Map(sortedEntries.map(([key, value]) => [formatTime(timeDimensionKey, key), value]));
}

const getChartValue = (row: ReportRowType, key: IRMetricKey, chartKeys: string[]) => {
  const currentMetricTotalValue =
    row[getTotalColumnNameByDimensions(chartKeys, key, chartKeys.length)];
  const comparisonMetricTotalValue =
    row[getPreviousTotalColumnNameByDimensions(chartKeys, key, chartKeys.length)];
  const currentMetricValue = Number(getMetricValue(row, key));
  const comparisonMetricValue = Number(getMetricPreviousValue(row, key) || 0);

  return {
    currentMetricValue: Number(currentMetricTotalValue || currentMetricValue),
    comparisonMetricValue: Number(comparisonMetricTotalValue || comparisonMetricValue),
  };
};

export const buildSeriesMap = (params: BuildSeriesMapParams): BuildSeriesMapResult => {
  const { data, metricKey, chartKeys, templateLabelById } = params;
  const seriesMap = new Map<SeriesKey, Map<DateKey, SeriesValue>>();
  const keysSet = new Set<string>();

  const firstChartKey = chartKeys[0];
  const secondChartKey = chartKeys[1];

  data.rows.forEach((row) => {
    const firstChartValue = String(row[firstChartKey] ?? NONE_VALUE);
    const currentKey = mapChartKeyToLabel(firstChartValue, templateLabelById);
    const comparisonKey = String(getMetricPreviousValue(row, firstChartKey) || currentKey);
    const { currentMetricValue, comparisonMetricValue } = getChartValue(row, metricKey, chartKeys);

    keysSet.add(currentKey);

    let seriesKey: string;
    if (!secondChartKey || secondChartKey === TIME_SERIES) {
      seriesKey = TIME_SERIES;
    } else if (row[secondChartKey]) {
      seriesKey = String(row[secondChartKey]);
    } else {
      seriesKey = NONE_VALUE;
      //Don't show series with none value
      return;
    }

    seriesKey = mapChartKeyToLabel(seriesKey, templateLabelById);

    if (!seriesMap.has(seriesKey)) {
      seriesMap.set(seriesKey, new Map());
    }

    const mapForSeries = seriesMap.get(seriesKey)!;
    if (!mapForSeries.has(currentKey)) {
      mapForSeries.set(currentKey, {
        currentMetricValue,
        comparisonKey,
        comparisonMetricValue,
      });
    }
  });
  const sortedSeriesMap = sortSeriesMap(seriesMap, NONE_VALUE, secondChartKey);

  return { seriesMap: sortedSeriesMap, keysSet, seriesKey: new Set(sortedSeriesMap.keys()) };
};

export const getAllDatesSorted = (keysSet: Set<string>, isTimeDimension?: boolean): string[] => {
  const allKeys = Array.from(keysSet);

  if (!isTimeDimension) {
    return allKeys;
  }
  return allKeys.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
};

export const buildSeriesNameByTime = (
  dataPointsOrDate: DataSeries['data'] | AnalyticDateFilter,
): string => {
  const DATE_FORMAT = 'MMM DD, YYYY';

  if (Array.isArray(dataPointsOrDate)) {
    if (!dataPointsOrDate.length) return '';

    const startKey = dataPointsOrDate[0].key;
    const endKey = dataPointsOrDate[dataPointsOrDate.length - 1].key;

    return formatDateRange(startKey, endKey, DATE_FORMAT);
  }

  const dateRange = getDateQueryVariable(dataPointsOrDate);
  return formatDateRange(dateRange.dateGTE, dateRange.dateLTE, DATE_FORMAT);
};

interface BuildDataSeriesOptions {
  seriesMap: SeriesMapType;
  allKeys: string[];
  showComparison?: boolean;
  dateFilter?: {
    currentDates: AnalyticDateFilter;
    previousDates: AnalyticDateFilter;
  };
}

const buildSeriesDataPair = (
  allKeys: string[],
  dateMap: Map<string, SeriesValue>,
): { currentData: DataSeries['data']; comparisonData: DataSeries['data'] } => {
  const currentData: DataSeries['data'] = [];
  const comparisonData: DataSeries['data'] = [];

  allKeys.forEach((key) => {
    const entry = dateMap.get(key);
    const currentMetricValue = entry?.currentMetricValue ?? 0;
    const comparisonMetricValue = entry?.comparisonMetricValue ?? 0;

    currentData.push({
      key,
      value: currentMetricValue,
      trend: {
        value: calculatePercentageChange(currentMetricValue, comparisonMetricValue),
        trend: currentMetricValue > comparisonMetricValue ? 'positive' : 'negative',
      },
    });

    comparisonData.push({
      key: entry?.comparisonKey ?? '',
      value: comparisonMetricValue,
    });
  });

  return { currentData, comparisonData };
};

const buildChartColor = (data: {
  seriesIndex: number;
  isNoDimension: boolean;
  isComparison: boolean;
  name: string;
}): string => {
  const { seriesIndex, isNoDimension, isComparison, name } = data;

  if (!isNoDimension)
    return CHART_COLOR_MAP[name] || CHART_COLORS[seriesIndex % CHART_COLORS.length];

  return isComparison ? COMPARISON_CHART_COLOR : SINGLE_CHART_COLOR;
};

const createSeriesObject = (data: {
  seriesKey: string;
  seriesData: DataSeries['data'];
  isComparison: boolean;
  seriesIndex: number;
  seriesCount: number;
  dateFilter?: BuildDataSeriesOptions['dateFilter'];
}): DataSeries => {
  const { seriesKey, seriesData, isComparison, seriesIndex, seriesCount, dateFilter } = data;
  const isNoDimension = seriesKey === TIME_SERIES;
  const name = buildSeriesName({ seriesKey, isComparison, seriesData, dateFilter });
  const color = buildChartColor({ seriesIndex, isNoDimension, isComparison, name });

  const series: DataSeries = {
    name,
    data: seriesData,
    color: isNoDimension ? (isComparison ? COMPARISON_CHART_COLOR : SINGLE_CHART_COLOR) : color,
  };

  if (isComparison) {
    series.isComparison = true;
  } else if (!isNoDimension) {
    series.metadata = { comparisonIndex: seriesIndex + seriesCount };
  }

  return series;
};

const buildSeriesName = (data: {
  seriesKey: string;
  isComparison: boolean;
  seriesData: DataSeries['data'];
  dateFilter?: BuildDataSeriesOptions['dateFilter'];
}): string => {
  const { seriesKey, isComparison, seriesData, dateFilter } = data;
  const isNoDimension = seriesKey === TIME_SERIES;

  if (!isNoDimension) {
    return isComparison ? `${seriesKey} (Previous)` : seriesKey;
  }

  if (dateFilter) {
    return buildSeriesNameByTime(isComparison ? dateFilter.previousDates : dateFilter.currentDates);
  }

  return buildSeriesNameByTime(seriesData);
};

export const buildDataSeries = (options: BuildDataSeriesOptions): DataSeries[] => {
  const { seriesMap, allKeys, showComparison = true, dateFilter } = options;

  const currentDataSeries: DataSeries[] = [];
  const comparisonDataSeries: DataSeries[] = [];
  const seriesCount = seriesMap.size;
  let seriesIndex = 0;

  seriesMap.forEach((dateMap, seriesKey) => {
    const { currentData, comparisonData } = buildSeriesDataPair(allKeys, dateMap);

    const currentSeries = createSeriesObject({
      seriesKey,
      seriesData: currentData,
      isComparison: false,
      seriesIndex,
      seriesCount,
      dateFilter,
    });
    currentDataSeries.push(currentSeries);

    if (showComparison) {
      const comparisonSeries = createSeriesObject({
        seriesKey,
        seriesData: comparisonData,
        isComparison: true,
        seriesIndex,
        seriesCount,
        dateFilter,
      });
      comparisonDataSeries.push(comparisonSeries);
    }

    seriesIndex++;
  });

  return [...currentDataSeries, ...comparisonDataSeries];
};
